"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
export interface ScrollToAnchorOptions {
  /** Offset in pixels to account for sticky headers */
  offset?: number;
  /** Scroll behavior for the action */
  behavior?: ScrollBehavior;
  /** Vertical alignment when using scrollIntoView */
  block?: ScrollLogicalPosition;
  /** Horizontal alignment when using scrollIntoView */
  inline?: ScrollLogicalPosition;
}
export interface UseScrollerOptions {
  /** Default offset in pixels applied to every scroll call */
  defaultOffset?: number;
  /** Default browser scroll behavior */
  defaultBehavior?: ScrollBehavior;
  /** Respect prefers-reduced-motion by using auto scrolling */
  respectReducedMotion?: boolean;
}
export interface UseScrollerReturn {
  /** Scrolls to an element id, with or without a leading # */
  scrollToAnchor: (anchor: string, options?: ScrollToAnchorOptions) => boolean;
  /** Scrolls to a hash string like #contact */
  scrollToHash: (hash: string, options?: ScrollToAnchorOptions) => boolean;
}
const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};
const normalizeAnchor = (value: string): string => value.startsWith("#") ? value.slice(1) : value;
const setHashInUrl = (anchor: string): void => {
  if (typeof window === "undefined") {
    return;
  }
  const nextHash = `#${anchor}`;
  if (window.location.hash === nextHash) {
    return;
  }
  window.history.pushState(window.history.state, "", nextHash);
};

/**
 * Hook for smooth-scrolling to in-page anchors and synchronizing the URL hash.
 *
 * @param options - Default scrolling configuration
 * @returns Functions for scrolling to anchor ids or hash values
 *
 * @example
 * ```tsx
 * const { scrollToAnchor } = useScroller({ defaultOffset: 96 });
 * <button onClick={() => scrollToAnchor("contact")}>Kontakt</button>
 * ```
 */
export function useScroller(t0) {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    defaultOffset: t2,
    defaultBehavior: t3,
    respectReducedMotion: t4
  } = options;
  const defaultOffset = t2 === undefined ? 0 : t2;
  const defaultBehavior = t3 === undefined ? "smooth" : t3;
  const respectReducedMotion = t4 === undefined ? true : t4;
  let t5;
  if ($[2] !== defaultBehavior || $[3] !== defaultOffset || $[4] !== respectReducedMotion) {
    t5 = (hash, t6) => {
      const scrollOptions = t6 === undefined ? {} : t6;
      if (typeof window === "undefined") {
        return false;
      }
      const anchor = normalizeAnchor(hash);
      if (!anchor) {
        return false;
      }
      const element = document.getElementById(anchor);
      if (!element) {
        return false;
      }
      setHashInUrl(anchor);
      const {
        offset: t7,
        behavior: t8,
        block: t9,
        inline: t10
      } = scrollOptions;
      const offset = t7 === undefined ? defaultOffset : t7;
      const behavior = t8 === undefined ? defaultBehavior : t8;
      const block = t9 === undefined ? "start" : t9;
      const inline = t10 === undefined ? "nearest" : t10;
      const resolvedBehavior = respectReducedMotion && prefersReducedMotion() ? "auto" : behavior;
      if (offset !== 0) {
        const absoluteTop = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: absoluteTop - offset,
          behavior: resolvedBehavior
        });
        return true;
      }
      element.scrollIntoView({
        behavior: resolvedBehavior,
        block,
        inline
      });
      return true;
    };
    $[2] = defaultBehavior;
    $[3] = defaultOffset;
    $[4] = respectReducedMotion;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const scrollToHash = t5;
  let t6;
  if ($[6] !== scrollToHash) {
    t6 = (anchor_0, scrollOptions_0) => scrollToHash(anchor_0, scrollOptions_0);
    $[6] = scrollToHash;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const scrollToAnchor = t6;
  let t7;
  if ($[8] !== scrollToAnchor || $[9] !== scrollToHash) {
    t7 = {
      scrollToAnchor,
      scrollToHash
    };
    $[8] = scrollToAnchor;
    $[9] = scrollToHash;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}